import React from "react";
import { combineEpics, ofType } from "redux-observable";
import * as Rx from "rxjs";
import * as RxOp from "rxjs/operators";
import {
  CREATE_ADMINISTERED_USER_SUCCEEDED,
  EDIT_ADMINISTERED_USER_SUCCEEDED,
  checkAdministeredUser,
  createAdministeredUser,
  editAdministeredUser,
  getAdministeredUserBeingEdited,
} from "@/components/administeredUsers/AdministeredUsers.state";
import {
  CLOSE_CREATE_USER_FORM,
  CLOSE_EDIT_USER_FORM,
  CLOSE_PERSONAL_INFO_FORM,
  OPEN_EDIT_USER_FORM,
  OPEN_TOAST_NOTIFICATION,
  SUBMIT_ADD_USER,
  SUBMIT_EDIT_USER,
  SUBMIT_PERSONAL_INFO,
  closeToastNotification,
  editInternalUserAlertBarAction,
  submitReactivateAdministeredUser,
} from "./UserSettingsPage.state";
import { addAlert, clearAlerts } from "@/components/alert-bar/AlertBar.state";
import { getCurrentUserClient } from "../currentUser/CurrentUser.state";
import { DEFAULT_TIMEZONE } from "@/util/dates";
import { scrollToTop } from "../../util/scrollTo.epic";
import { SELECT_ALL_SLUG } from "@/sanctuary/typedefs";
import { buildAgenciesForRole } from "@/util/common";
import {
  userWithStatusCanBeActivated,
  userWithStatusCanBeReactivated,
} from "../administeredUsers/AdministeredUsers.state";
import { ButtonWithAction, Text } from "@thecb/components";

export const buildAddUserPayload = (state) => {
  const role = state.userSettingsPage.forms.addUserInfoForm.role.rawValue;
  const pos_role = state.userSettingsPage.forms.addUserInfoForm.pos_role?.rawValue;
  const allSelected = state.userSettingsPage.allSelected;

  const agencies = buildAgenciesForRole(
    allSelected
      ? [{ slug: SELECT_ALL_SLUG }]
      : state.userSettingsPage.selectedAgencies,
    role,
    pos_role
  ).map((agency) => agency.slug);

  return {
    firstName: state.userSettingsPage.forms.addUserInfoForm.firstName.rawValue,
    lastName: state.userSettingsPage.forms.addUserInfoForm.lastName.rawValue,
    email: state.userSettingsPage.forms.addUserInfoForm.email.rawValue?.toLowerCase(),
    role,
    pos_role,
    timezone: DEFAULT_TIMEZONE,
    agencies: allSelected
      ? agencies
      : agencies.filter((slug) => slug !== SELECT_ALL_SLUG),
  };
};

const buildEditUserPayload = (state) => {
  const role = state.userSettingsPage.forms.editUserInfoForm.role.rawValue;
  const pos_role = state.userSettingsPage.forms.editUserInfoForm.pos_role?.rawValue;
  const allSelected = state.userSettingsPage.allSelected;

  const agencies = buildAgenciesForRole(
    allSelected
      ? [{ slug: SELECT_ALL_SLUG }]
      : state.userSettingsPage.selectedAgencies,
    role,
    pos_role
  ).map((agency) => agency.slug);

  return {
    userId: getAdministeredUserBeingEdited(state).value.id.value,
    firstName: state.userSettingsPage.forms.editUserInfoForm.firstName.rawValue,
    lastName: state.userSettingsPage.forms.editUserInfoForm.lastName.rawValue,
    isActive: state.userSettingsPage.forms.editUserInfoForm.isActive.rawValue,
    role,
    pos_role,
    timezone: DEFAULT_TIMEZONE,
    agencies: allSelected
      ? agencies
      : agencies.filter((slug) => slug !== SELECT_ALL_SLUG),
    client: getCurrentUserClient(state),
  };
};

export const buildPersonalInfoPayload = (state) => ({
  email: state.userSettingsPage.forms.personalInfoForm.email.rawValue?.toLowerCase(),
  firstName: state.userSettingsPage.forms.personalInfoForm.firstName.rawValue,
  lastName: state.userSettingsPage.forms.personalInfoForm.lastName.rawValue,
});

const addUserInfoEpic = (action$, state$) =>
  action$.pipe(
    ofType(SUBMIT_ADD_USER),
    RxOp.flatMap(() =>
      Rx.of(createAdministeredUser(buildAddUserPayload(state$.value)))
    )
  );

export const editUserInfoEpic = (action$, state$) =>
  action$.pipe(
    ofType(SUBMIT_EDIT_USER),
    RxOp.flatMap(() =>
      Rx.of(editAdministeredUser(buildEditUserPayload(state$.value)))
    )
  );

export const personalInfoEpic = (action$, state$) =>
  action$.pipe(
    ofType(SUBMIT_PERSONAL_INFO),
    RxOp.flatMap(() =>
      Rx.of(checkAdministeredUser(buildPersonalInfoPayload(state$.value)))
    )
  );

const openEditUserFormEpic = (action$, state$) =>
  action$.pipe(
    ofType(OPEN_EDIT_USER_FORM),
    RxOp.mergeMap(() => {
      const userBeingEdited = getAdministeredUserBeingEdited(state$.value)?.value;
      const isActive = userBeingEdited?.isActive?.value;
      const userStatus = userBeingEdited?.status?.value;

      return Rx.concat([
        Rx.of(scrollToTop(), editInternalUserAlertBarAction(clearAlerts())),
        Rx.iif(
          () => !isActive && userWithStatusCanBeActivated(userStatus),
          Rx.of(
            editInternalUserAlertBarAction(
              addAlert({
                heading: "Deactivated User Not Editable",
                text: `This user has been deactivated. You cannot make any changes to their settings until they have been reactivated.`,
                variant: "info",
              })
            )
          ),
          Rx.EMPTY
        ),
        Rx.iif(
          () => !isActive && userWithStatusCanBeReactivated(userStatus),
          Rx.of(
            editInternalUserAlertBarAction(
              addAlert({
                heading: "User Has Not Activated Account",
                variant: "info",
                textWithDispatch: (dispatch) => (
                  <Text>
                    This user has not activated their account yet.
                    <ButtonWithAction
                      extraStyles="margin: 0px;"
                      variant="smallGhost"
                      text="Resend the activation link"
                      action={() => {
                        dispatch(
                          submitReactivateAdministeredUser({
                            userId: userBeingEdited?.id?.value,
                          })
                        );
                      }}
                    />
                  </Text>
                ),
              })
            )
          ),
          Rx.EMPTY
        ),
      ]);
    }),
    RxOp.concatAll()
  );

const closeInternalUserFormEpic = (action$) =>
  action$.pipe(
    ofType(
      CLOSE_CREATE_USER_FORM,
      CLOSE_EDIT_USER_FORM,
      CLOSE_PERSONAL_INFO_FORM,
      CREATE_ADMINISTERED_USER_SUCCEEDED,
      EDIT_ADMINISTERED_USER_SUCCEEDED
    ),
    RxOp.flatMap(() => Rx.of(scrollToTop()))
  );

export const openToastNotificationEpic = (action$) =>
  action$.pipe(
    ofType(OPEN_TOAST_NOTIFICATION),
    RxOp.flatMap(() => Rx.of(closeToastNotification()).pipe(RxOp.delay(5000)))
  );

export default combineEpics(
  addUserInfoEpic,
  closeInternalUserFormEpic,
  editUserInfoEpic,
  openEditUserFormEpic,
  openToastNotificationEpic,
  personalInfoEpic
);
